/** "Route to nearest exit" on the member floorplan (#473): the page logic, kept pure for tests. */

import type { NearestExitKind } from "@/lib/api/client";

/** The `?exit=` deep-link value → the search kind, or null when the link asks for no exit route. */
export function parseExitParam(value: string | null): NearestExitKind | null {
  if (value === "emergency") return "emergency";
  if (value === "any" || value === "") return "any";
  return null;
}

/** The kind the "Emergency route" switch asks for. */
export function exitKind(emergencyOnly: boolean): NearestExitKind {
  return emergencyOnly ? "emergency" : "any";
}

/** The button's label: what it finds, or that it is finding it. */
export function nearestExitButtonLabel(kind: NearestExitKind, pending: boolean): string {
  if (pending) return "Finding the nearest exit...";
  return kind === "emergency" ? "Nearest exit in an emergency" : "Nearest exit";
}

/** What the member reads when no exit route comes back. The backend's `detail`
 *  (no exits marked, out-of-service stairs, no room, …) is already worded for
 *  them; anything else is a generic retry. */
export function nearestExitErrorMessage(err: unknown, kind: NearestExitKind): string {
  const response = (err as { response?: { status?: number; data?: { detail?: unknown } } } | null)?.response;
  const detail = response?.data?.detail;
  if (response?.status === 400 && typeof detail === "string" && detail.includes("no assigned room")) {
    return "Pick a room on the plan first, then tap Nearest exit from here: we don't know which room you're in.";
  }
  if (response?.status && response.status >= 400 && response.status < 500 && typeof detail === "string" && detail) {
    return detail;
  }
  return kind === "emergency"
    ? "Couldn't find an emergency route to an exit. Please try again."
    : "Couldn't find a route to an exit. Please try again.";
}

/** The page URL's params once an exit route is shown (`?exit=` + `from`), replacing a room route's `to`. */
export function withExitParams(search: string, kind: NearestExitKind, fromRoomId?: number | null): URLSearchParams {
  const params = new URLSearchParams(search);
  params.delete("to");
  params.set("exit", kind);
  if (fromRoomId) params.set("from", String(fromRoomId));
  else params.delete("from");
  return params;
}
